import { useState, useCallback, useEffect, useMemo, useRef, ReactNode } from "react";
import { createPortal } from "react-dom";
import { APIError, useAxios } from "../core/_axios";
import { getSignaturesByAccount, AccountBreakdown } from "../core/_requests_v2";
import LoadingSpinner from "./LoadingSpinner";
import { OnboardingProjection } from "./OnboardingProjection";
import dayjs from "dayjs";

type DatePreset = '6mo' | '12mo' | 'all';

function CellTooltip({ children, tooltip }: { children: ReactNode; tooltip: ReactNode }) {
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    const cellRef = useRef<HTMLTableCellElement>(null);

    const show = () => {
        if (!cellRef.current) return;
        const rect = cellRef.current.getBoundingClientRect();
        setPos({ x: rect.right, y: rect.bottom + 4 });
    };
    const hide = () => setPos(null);

    return (
        <td
            ref={cellRef}
            className="px-4 py-3 text-right"
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            {children}
            {pos && createPortal(
                <div
                    className="fixed z-[9999] pointer-events-none"
                    style={{ top: pos.y, left: pos.x, transform: 'translateX(-100%)' }}
                >
                    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                        {tooltip}
                    </div>
                </div>,
                document.body
            )}
        </td>
    );
}

export function SignaturesByAccount() {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [accounts, setAccounts] = useState<AccountBreakdown[]>([]);
    const [apiStartDate, setApiStartDate] = useState<string>('');
    const [apiEndDate, setApiEndDate] = useState<string>('');
    const [totalAccounts, setTotalAccounts] = useState<number>(0);
    const [totalSignatures, setTotalSignatures] = useState<number>(0);
    const [datePreset, setDatePreset] = useState<DatePreset>('12mo');
    const [topN, setTopN] = useState<number>(5);

    // Configurable assumptions
    const [closeRate, setCloseRate] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesByAccount-closeRate');
        return stored ? Number(stored) : 35;
    });
    const [policyValue, setPolicyValue] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesGraph-policyValue');
        return stored ? Number(stored) : 5500;
    });
    const [insuranceMargin, setInsuranceMargin] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesGraph-insuranceMargin');
        return stored ? Number(stored) : 25;
    });
    const [generalConversion, setGeneralConversion] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesByAccount-generalConversion');
        return stored ? Number(stored) : 2;
    });
    const [collectionRate, setCollectionRate] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesByAccount-collectionRate');
        return stored ? Number(stored) : 85;
    });

    // Popover state
    const [showCloseRatePopover, setShowCloseRatePopover] = useState(false);
    const [popoverCloseRate, setPopoverCloseRate] = useState<number>(closeRate);
    const closeRatePopoverRef = useRef<HTMLDivElement>(null);

    const [showPolicyPopover, setShowPolicyPopover] = useState(false);
    const [popoverPolicyValue, setPopoverPolicyValue] = useState<number>(policyValue);
    const policyPopoverRef = useRef<HTMLDivElement>(null);

    const [showMarginPopover, setShowMarginPopover] = useState(false);
    const [popoverMarginValue, setPopoverMarginValue] = useState<number>(insuranceMargin);
    const marginPopoverRef = useRef<HTMLDivElement>(null);

    const [showGeneralPopover, setShowGeneralPopover] = useState(false);
    const [popoverGeneralValue, setPopoverGeneralValue] = useState<number>(generalConversion);
    const generalPopoverRef = useRef<HTMLDivElement>(null);

    const [showCollectionPopover, setShowCollectionPopover] = useState(false);
    const [popoverCollectionValue, setPopoverCollectionValue] = useState<number>(collectionRate);
    const collectionPopoverRef = useRef<HTMLDivElement>(null);

    const { startDate, endDate } = useMemo(() => {
        const today = dayjs();
        switch (datePreset) {
            case '6mo':
                return { startDate: today.subtract(6, 'month').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
            case '12mo':
                return { startDate: today.subtract(12, 'month').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
            case 'all':
                return { startDate: undefined, endDate: undefined };
            default:
                return { startDate: today.subtract(12, 'month').format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
        }
    }, [datePreset]);

    const loadData = useCallback(async () => {
        setLoadingError(null);
        setLoading(true);
        try {
            const result = await getSignaturesByAccount(axios, startDate, endDate, topN);
            setAccounts(result.accounts);
            setTotalAccounts(result.total_accounts);
            setTotalSignatures(result.total_signatures);
            setApiStartDate(result.start_date);
            setApiEndDate(result.end_date);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError
                    ? exception.message
                    : "An unknown error has occurred. Please try again.";
            setLoadingError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [axios, startDate, endDate, topN]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Close popovers on outside click or Escape
    useEffect(() => {
        if (!showCloseRatePopover) return;
        const handleClick = (e: MouseEvent) => {
            if (closeRatePopoverRef.current && !closeRatePopoverRef.current.contains(e.target as Node)) {
                setShowCloseRatePopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowCloseRatePopover(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
    }, [showCloseRatePopover]);

    useEffect(() => {
        if (!showPolicyPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (policyPopoverRef.current && !policyPopoverRef.current.contains(e.target as Node)) {
                setShowPolicyPopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowPolicyPopover(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
    }, [showPolicyPopover]);

    useEffect(() => {
        if (!showMarginPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (marginPopoverRef.current && !marginPopoverRef.current.contains(e.target as Node)) {
                setShowMarginPopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowMarginPopover(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
    }, [showMarginPopover]);

    useEffect(() => {
        if (!showGeneralPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (generalPopoverRef.current && !generalPopoverRef.current.contains(e.target as Node)) {
                setShowGeneralPopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowGeneralPopover(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
    }, [showGeneralPopover]);

    useEffect(() => {
        if (!showCollectionPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (collectionPopoverRef.current && !collectionPopoverRef.current.contains(e.target as Node)) {
                setShowCollectionPopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowCollectionPopover(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
    }, [showCollectionPopover]);

    // Value calculations per account
    const accountsWithValues = useMemo(() => {
        const rate = closeRate / 100;
        const GENERAL_CONVERSION_RATE = generalConversion / 100;
        const marginRate = insuranceMargin / 100;
        const collectionFactor = collectionRate / 100;

        return accounts.map((acct) => {
            const highIntentConversions = Math.round(acct.preneed_count * rate);
            const generalConversions = Math.round((acct.signature_count - acct.preneed_count) * GENERAL_CONVERSION_RATE);
            const totalConversions = highIntentConversions + generalConversions;
            const revenue = totalConversions * policyValue * collectionFactor;
            const profit = Math.round(revenue * marginRate);
            const preneedPct = acct.signature_count > 0 ? (acct.preneed_count / acct.signature_count * 100) : 0;

            return {
                ...acct,
                highIntentConversions,
                generalConversions,
                totalConversions,
                revenue,
                profit,
                preneedPct,
            };
        });
    }, [accounts, closeRate, policyValue, insuranceMargin, generalConversion, collectionRate]);

    // Totals
    const totals = useMemo(() => {
        return accountsWithValues.reduce(
            (acc, row) => ({
                service_count: acc.service_count + row.service_count,
                signature_count: acc.signature_count + row.signature_count,
                preneed_count: acc.preneed_count + row.preneed_count,
                totalConversions: acc.totalConversions + row.totalConversions,
                revenue: acc.revenue + row.revenue,
                profit: acc.profit + row.profit,
            }),
            { service_count: 0, signature_count: 0, preneed_count: 0, totalConversions: 0, revenue: 0, profit: 0 }
        );
    }, [accountsWithValues]);

    const totalPreneedPct = totals.signature_count > 0 ? (totals.preneed_count / totals.signature_count * 100) : 0;

    // Popover apply/reset helpers
    const applyCloseRate = () => {
        if (popoverCloseRate > 0 && popoverCloseRate <= 100) {
            setCloseRate(popoverCloseRate);
            localStorage.setItem('signaturesByAccount-closeRate', String(popoverCloseRate));
        }
        setShowCloseRatePopover(false);
    };
    const resetCloseRate = () => {
        setCloseRate(35);
        setPopoverCloseRate(35);
        localStorage.setItem('signaturesByAccount-closeRate', '35');
        setShowCloseRatePopover(false);
    };

    const applyPolicyValue = () => {
        if (popoverPolicyValue > 0) {
            setPolicyValue(popoverPolicyValue);
            localStorage.setItem('signaturesGraph-policyValue', String(popoverPolicyValue));
        }
        setShowPolicyPopover(false);
    };
    const resetPolicyValue = () => {
        setPolicyValue(5500);
        setPopoverPolicyValue(5500);
        localStorage.setItem('signaturesGraph-policyValue', '5500');
        setShowPolicyPopover(false);
    };

    const applyMarginValue = () => {
        if (popoverMarginValue > 0 && popoverMarginValue <= 100) {
            setInsuranceMargin(popoverMarginValue);
            localStorage.setItem('signaturesGraph-insuranceMargin', String(popoverMarginValue));
        }
        setShowMarginPopover(false);
    };
    const resetMarginValue = () => {
        setInsuranceMargin(25);
        setPopoverMarginValue(25);
        localStorage.setItem('signaturesGraph-insuranceMargin', '25');
        setShowMarginPopover(false);
    };

    const applyGeneralConversion = () => {
        if (popoverGeneralValue > 0 && popoverGeneralValue <= 100) {
            setGeneralConversion(popoverGeneralValue);
            localStorage.setItem('signaturesByAccount-generalConversion', String(popoverGeneralValue));
        }
        setShowGeneralPopover(false);
    };
    const resetGeneralConversion = () => {
        setGeneralConversion(2);
        setPopoverGeneralValue(2);
        localStorage.setItem('signaturesByAccount-generalConversion', '2');
        setShowGeneralPopover(false);
    };

    const applyCollectionRate = () => {
        if (popoverCollectionValue > 0 && popoverCollectionValue <= 100) {
            setCollectionRate(popoverCollectionValue);
            localStorage.setItem('signaturesByAccount-collectionRate', String(popoverCollectionValue));
        }
        setShowCollectionPopover(false);
    };
    const resetCollectionRate = () => {
        setCollectionRate(85);
        setPopoverCollectionValue(85);
        localStorage.setItem('signaturesByAccount-collectionRate', '85');
        setShowCollectionPopover(false);
    };

    const formatCurrency = (value: number) => {
        if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
        if (value >= 1000) return `$${Math.round(value / 1000).toLocaleString()}K`;
        return `$${value.toLocaleString()}`;
    };

    const formatCurrencyExact = (value: number) => `$${value.toLocaleString()}`;

    const sigPerService = (sigs: number, services: number) =>
        services > 0 ? (sigs / services).toFixed(1) : '0';

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => { setLoadingError(null); loadData(); }}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const controlsAndTable = (
        <>
            {/* Controls row */}
            <div className="card mb-4">
                <div className="card-body py-3">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Date range presets */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">Period:</span>
                            <div className="flex gap-1">
                                {([
                                    { key: '6mo' as DatePreset, label: '6 Months' },
                                    { key: '12mo' as DatePreset, label: '12 Months' },
                                    { key: 'all' as DatePreset, label: 'All Time' },
                                ]).map((preset) => (
                                    <button
                                        key={preset.key}
                                        onClick={() => setDatePreset(preset.key)}
                                        className={`px-3 py-1 rounded text-xs cursor-pointer transition-colors ${
                                            datePreset === preset.key
                                                ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Top N selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">Top:</span>
                            <div className="flex gap-1">
                                {[5, 10, 25, 50].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => setTopN(n)}
                                        className={`px-2.5 py-1 rounded text-xs cursor-pointer transition-colors ${
                                            topN === n
                                                ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="border-l border-gray-200 h-6" />

                        {/* Configurable assumptions */}
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="font-medium">Assumptions:</span>

                            {/* Close rate */}
                            <span className="relative inline-block">
                                <span
                                    className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                    onClick={() => { setPopoverCloseRate(closeRate); setShowCloseRatePopover(!showCloseRatePopover); }}
                                >
                                    {closeRate}% close rate
                                </span>
                                {showCloseRatePopover && (
                                    <div ref={closeRatePopoverRef} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-56">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">High-Intent Close Rate (%)</label>
                                        <input
                                            type="number"
                                            value={popoverCloseRate}
                                            onChange={(e) => setPopoverCloseRate(Number(e.target.value))}
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                            autoFocus
                                            min={1}
                                            max={100}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyCloseRate(); }}
                                        />
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {[{ label: '20%', value: 20 }, { label: '35%', value: 35 }, { label: '50%', value: 50 }].map((p) => (
                                                <button
                                                    key={p.value}
                                                    onClick={() => { setPopoverCloseRate(p.value); setCloseRate(p.value); localStorage.setItem('signaturesByAccount-closeRate', String(p.value)); }}
                                                    className={`px-2 py-1 rounded text-xs cursor-pointer ${closeRate === p.value ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button onClick={resetCloseRate} className="text-xs text-gray-500 hover:text-gray-700">Reset</button>
                                            <button onClick={applyCloseRate} className="btn btn-sm btn-primary">Apply</button>
                                        </div>
                                    </div>
                                )}
                            </span>

                            <span className="text-gray-300">|</span>

                            {/* Policy value */}
                            <span className="relative inline-block">
                                <span
                                    className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                    onClick={() => { setPopoverPolicyValue(policyValue); setShowPolicyPopover(!showPolicyPopover); }}
                                >
                                    ${policyValue >= 1000 ? `${(policyValue / 1000).toFixed(policyValue % 1000 === 0 ? 0 : 1)}K` : policyValue.toLocaleString()} policy
                                </span>
                                {showPolicyPopover && (
                                    <div ref={policyPopoverRef} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-56">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Avg Policy Value ($)</label>
                                        <input
                                            type="number"
                                            value={popoverPolicyValue}
                                            onChange={(e) => setPopoverPolicyValue(Number(e.target.value))}
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                            autoFocus
                                            min={100}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyPolicyValue(); }}
                                        />
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {[{ label: '$3K', value: 3000 }, { label: '$5.5K', value: 5500 }, { label: '$8K', value: 8000 }, { label: '$10K', value: 10000 }].map((p) => (
                                                <button
                                                    key={p.value}
                                                    onClick={() => { setPopoverPolicyValue(p.value); setPolicyValue(p.value); localStorage.setItem('signaturesGraph-policyValue', String(p.value)); }}
                                                    className={`px-2 py-1 rounded text-xs cursor-pointer ${policyValue === p.value ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button onClick={resetPolicyValue} className="text-xs text-gray-500 hover:text-gray-700">Reset</button>
                                            <button onClick={applyPolicyValue} className="btn btn-sm btn-primary">Apply</button>
                                        </div>
                                    </div>
                                )}
                            </span>

                            <span className="text-gray-300">|</span>

                            {/* Insurance margin */}
                            <span className="relative inline-block">
                                <span
                                    className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                    onClick={() => { setPopoverMarginValue(insuranceMargin); setShowMarginPopover(!showMarginPopover); }}
                                >
                                    {insuranceMargin}% margin
                                </span>
                                {showMarginPopover && (
                                    <div ref={marginPopoverRef} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-56">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Insurance Margin (%)</label>
                                        <input
                                            type="number"
                                            value={popoverMarginValue}
                                            onChange={(e) => setPopoverMarginValue(Number(e.target.value))}
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                            autoFocus
                                            min={1}
                                            max={100}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyMarginValue(); }}
                                        />
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {[{ label: '10%', value: 10 }, { label: '25%', value: 25 }, { label: '35%', value: 35 }, { label: '50%', value: 50 }].map((p) => (
                                                <button
                                                    key={p.value}
                                                    onClick={() => { setPopoverMarginValue(p.value); setInsuranceMargin(p.value); localStorage.setItem('signaturesGraph-insuranceMargin', String(p.value)); }}
                                                    className={`px-2 py-1 rounded text-xs cursor-pointer ${insuranceMargin === p.value ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button onClick={resetMarginValue} className="text-xs text-gray-500 hover:text-gray-700">Reset</button>
                                            <button onClick={applyMarginValue} className="btn btn-sm btn-primary">Apply</button>
                                        </div>
                                    </div>
                                )}
                            </span>

                            <span className="text-gray-300">|</span>

                            {/* General conversion */}
                            <span className="relative inline-block">
                                <span
                                    className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                    onClick={() => { setPopoverGeneralValue(generalConversion); setShowGeneralPopover(!showGeneralPopover); }}
                                >
                                    {generalConversion}% general conv.
                                </span>
                                {showGeneralPopover && (
                                    <div ref={generalPopoverRef} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-56">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">General Conversion Rate (%)</label>
                                        <input
                                            type="number"
                                            value={popoverGeneralValue}
                                            onChange={(e) => setPopoverGeneralValue(Number(e.target.value))}
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                            autoFocus
                                            min={0.1}
                                            max={100}
                                            step={0.1}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyGeneralConversion(); }}
                                        />
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {[{ label: '0.5%', value: 0.5 }, { label: '1%', value: 1 }, { label: '2%', value: 2 }, { label: '3%', value: 3 }].map((p) => (
                                                <button
                                                    key={p.value}
                                                    onClick={() => { setPopoverGeneralValue(p.value); setGeneralConversion(p.value); localStorage.setItem('signaturesByAccount-generalConversion', String(p.value)); }}
                                                    className={`px-2 py-1 rounded text-xs cursor-pointer ${generalConversion === p.value ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button onClick={resetGeneralConversion} className="text-xs text-gray-500 hover:text-gray-700">Reset</button>
                                            <button onClick={applyGeneralConversion} className="btn btn-sm btn-primary">Apply</button>
                                        </div>
                                    </div>
                                )}
                            </span>

                            <span className="text-gray-300">|</span>

                            {/* Collection rate */}
                            <span className="relative inline-block">
                                <span
                                    className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                    onClick={() => { setPopoverCollectionValue(collectionRate); setShowCollectionPopover(!showCollectionPopover); }}
                                >
                                    {collectionRate}% collected
                                </span>
                                {showCollectionPopover && (
                                    <div ref={collectionPopoverRef} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-56">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Premium Collection Rate (%)</label>
                                        <input
                                            type="number"
                                            value={popoverCollectionValue}
                                            onChange={(e) => setPopoverCollectionValue(Number(e.target.value))}
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                            autoFocus
                                            min={1}
                                            max={100}
                                            onKeyDown={(e) => { if (e.key === 'Enter') applyCollectionRate(); }}
                                        />
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {[{ label: '75%', value: 75 }, { label: '85%', value: 85 }, { label: '95%', value: 95 }].map((p) => (
                                                <button
                                                    key={p.value}
                                                    onClick={() => { setPopoverCollectionValue(p.value); setCollectionRate(p.value); localStorage.setItem('signaturesByAccount-collectionRate', String(p.value)); }}
                                                    className={`px-2 py-1 rounded text-xs cursor-pointer ${collectionRate === p.value ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button onClick={resetCollectionRate} className="text-xs text-gray-500 hover:text-gray-700">Reset</button>
                                            <button onClick={applyCollectionRate} className="btn btn-sm btn-primary">Apply</button>
                                        </div>
                                    </div>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Platform context */}
            {accounts.length > 0 && totalAccounts > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
                    <div className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-800">
                            Showing <span className="font-semibold">top {accounts.length}</span> accounts
                            by signature volume out of <span className="font-semibold">{totalAccounts.toLocaleString()} active accounts</span> on
                            the platform ({totalSignatures.toLocaleString()} total signatures in this period).
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            {accounts.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-10">
                        <p className="text-gray-500">No signature data found for this period.</p>
                    </div>
                </div>
            ) : (
                <div className="card">
                    <div className="card-body p-0">
                        <div>
                            <table className="table table-auto w-full">
                                <thead>
                                    <tr className="text-xs text-gray-500 uppercase">
                                        <th className="px-4 py-3 text-left w-10">#</th>
                                        <th className="px-4 py-3 text-left">Account</th>
                                        <th className="px-4 py-3 text-right">Services</th>
                                        <th className="px-4 py-3 text-right">Signatures</th>
                                        <th className="px-4 py-3 text-right">Preneed</th>
                                        <th className="px-4 py-3 text-right">Proj. Policies</th>
                                        <th className="px-4 py-3 text-right">Revenue</th>
                                        <th className="px-4 py-3 text-right">Profit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accountsWithValues.map((row, i) => (
                                        <tr key={row.account_id} className="border-t border-gray-100 hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-medium text-gray-900">{row.account_name}</div>
                                                <div className="text-xs text-gray-400">{row.account_unique_identifier}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right text-gray-700">{row.service_count.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="text-sm text-gray-700">{row.signature_count.toLocaleString()}</div>
                                                <div className="text-xs text-gray-400">{sigPerService(row.signature_count, row.service_count)}/service</div>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="text-sm text-gray-700">{row.preneed_count.toLocaleString()}</div>
                                                <div className="text-xs text-gray-400">{row.preneedPct.toFixed(1)}%</div>
                                            </td>
                                            <CellTooltip tooltip={<>
                                                <div className="font-medium mb-1">Projected Policies Breakdown</div>
                                                <div>{row.preneed_count.toLocaleString()} preneed x {closeRate}% close = {row.highIntentConversions}</div>
                                                <div>{(row.signature_count - row.preneed_count).toLocaleString()} general x {generalConversion}% = {row.generalConversions}</div>
                                                <div className="border-t border-gray-600 mt-1 pt-1 font-medium">Total: {row.totalConversions}</div>
                                            </>}>
                                                <div className="text-sm text-gray-700">{row.totalConversions.toLocaleString()}</div>
                                                <div className="text-xs text-gray-400">{row.service_count > 0 ? (row.totalConversions / row.service_count).toFixed(1) : '0'}/service</div>
                                            </CellTooltip>
                                            <CellTooltip tooltip={<>
                                                <div className="font-medium mb-1">Revenue Breakdown</div>
                                                <div>{row.totalConversions} policies x {formatCurrencyExact(policyValue)} face value x {collectionRate}% collected</div>
                                                <div className="border-t border-gray-600 mt-1 pt-1 font-medium">= {formatCurrencyExact(row.revenue)}</div>
                                            </>}>
                                                <div className="text-sm text-gray-700">{formatCurrency(row.revenue)}</div>
                                            </CellTooltip>
                                            <CellTooltip tooltip={<>
                                                <div className="font-medium mb-1">Profit Breakdown</div>
                                                <div>{formatCurrencyExact(row.revenue)} revenue x {insuranceMargin}% margin</div>
                                                <div className="border-t border-gray-600 mt-1 pt-1 font-medium">= {formatCurrencyExact(row.profit)}</div>
                                            </>}>
                                                <div className="text-sm font-medium text-green-700">{formatCurrency(row.profit)}</div>
                                            </CellTooltip>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                                        <td className="px-4 py-3"></td>
                                        <td className="px-4 py-3 text-sm text-gray-700">Total ({accountsWithValues.length} accounts)</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-700">{totals.service_count.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="text-sm text-gray-700">{totals.signature_count.toLocaleString()}</div>
                                            <div className="text-xs text-gray-400 font-normal">{sigPerService(totals.signature_count, totals.service_count)}/service</div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="text-sm text-gray-700">{totals.preneed_count.toLocaleString()}</div>
                                            <div className="text-xs text-gray-400 font-normal">{totalPreneedPct.toFixed(1)}%</div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="text-sm text-gray-700">{totals.totalConversions.toLocaleString()}</div>
                                            <div className="text-xs text-gray-400 font-normal">{sigPerService(totals.totalConversions, totals.service_count)}/service</div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(totals.revenue)}</td>
                                        <td className="px-4 py-3 text-sm text-right font-medium text-green-700">{formatCurrency(totals.profit)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div>
            {accounts.length > 0 && apiStartDate && apiEndDate ? (
                <OnboardingProjection
                    accounts={accounts}
                    accountTableData={accountsWithValues}
                    startDate={apiStartDate}
                    endDate={apiEndDate}
                    closeRate={closeRate}
                    policyValue={policyValue}
                    insuranceMargin={insuranceMargin}
                    generalConversion={generalConversion}
                    collectionRate={collectionRate}
                    totalAccounts={totalAccounts}
                    totalSignatures={totalSignatures}
                >
                    {controlsAndTable}
                </OnboardingProjection>
            ) : (
                controlsAndTable
            )}
        </div>
    );
}
